<template>
  <div class="waylist list" v-if="way">
    <!-- START ELEMENT -->
    <article>
      <label class="label">{{ $t("labels.startElement") }}</label>

      <item-combo-select
        v-if="way.startItemId === null"
        :targets="items.target"
        :resources="{}"
        :indices="{}"
        :hide-selected="true"
        :net-choosable="false"
        @select-single="addStartItem($event)"
      />
      <div v-else>
        <!-- START ELEMENT -->
        <button-item
          :icons="relations.length === 0 ? ['x'] : []"
          @click="openInOverlay($event, startItem)"
          @click:x="removeStartItem"
        >
          {{ itemTitles[way.startItemType][way.startItemId] }}
        </button-item>

        <!-- CHANGE IMPULSE -->
        <div class="waylist__changeimpulse">
          <div>
            <div>{{ $t("labels.changeImpulse") }}</div>
            <div class="text-annotation">
              ({{ $t("labels.startElementState") }})
            </div>
          </div>
          <button-item class="button-item_toggle" @click="switchValueOperator">
            {{ valueOperatorLabel }}
          </button-item>
          <input-number
            :value="absChange"
            :max="maxStartElementChange"
            step="any"
            @update:value="throttledSetStartItemChange"
          />
          <span>%</span>
        </div>
      </div>
    </article>

    <way-list-element
      v-for="(relation, index) in relations"
      :key="relation.id + '' + index"
      :start-item-id="relation.startItemId"
      :start-item-type="relation.startItemType"
      :end-item-id="relation.endItemId"
      :end-item-type="relation.endItemType"
      :round-index="index"
      :can-remove="index === relations.length - 1"
      :item-title="itemTitles[relation.endItemType][relation.endItemId]"
      :way-impulses="wayImpulses"
      @open="
        openInOverlay($event, items[relation.endItemType][relation.endItemId])
      "
      @remove="removeLastRelation"
    />

    <div v-if="nextPossibleEndItems.length > 0 && !way.complete">
      <h4 class="label">
        {{ $t("labels.nextElement") }}
      </h4>
      <input-dropdown
        :options="nextPossibleEndItems"
        prop="title"
        :clear-on-select="true"
        @select="addItem($event.item)"
      />
    </div>
    <div v-if="startItem != null">
      <input-switch
        :label="$t('labels.completeWay')"
        :value="way.complete"
        @update:value="setWayComplete"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { throttle, mapValues } from "lodash-es";

import InputDropdown from "@core/input/InputDropdown.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputSwitch from "@core/input/InputSwitch.vue";

import WayListElement from "@editor/items/way/WayListElement.vue";

import { useWayStore } from "@editor/items/way/ways.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useResourceStore } from "../resource/resources.store";

import type { Handleable } from "@core/types/Events";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { RelationItem } from "../relation/RelationItem";
import type { TargetItem } from "../target/TargetItem";

const props = defineProps<{
  wayId: number | null;
}>();

const wayStore = useWayStore();
const relationStore = useRelationStore();

const way = computed(() => {
  if (props.wayId == null) return null;
  return wayStore.items[props.wayId];
});

const items = computed(() => {
  return {
    target: useTargetStore().items,
    resource: useResourceStore().items
  };
});

const itemTitles = computed(() => {
  return mapValues(items.value, (itemsByType) =>
    mapValues(itemsByType, (e) => e.title)
  );
});

const relations = computed(() => {
  if (way.value == null) return [];
  return way.value.relationIds.map((id) => relationStore.items[id]);
});

const startItem = computed(() => {
  if (way.value == null || way.value.startItemId == null) return null;
  return items.value[way.value.startItemType][way.value.startItemId];
});

const lastWayItem = computed(() => {
  if (way.value == null) return null;
  if (way.value.startItemId == null) return null;
  if (relations.value.length > 0) {
    const relation = relations.value[relations.value.length - 1];
    return items.value[relation.endItemType][relation.endItemId];
  }
  return startItem.value;
});

const nextPossibleEndItems = computed(() => {
  const lastItem = lastWayItem.value;
  if (lastItem == null) return [];

  const relations = relationStore.orderedItems.filter(
    (relation) =>
      relation.updateType != "none" &&
      relation.endItemType == "target" &&
      relation.startItemType == lastItem.type &&
      relation.startItemId == lastItem.id
  );
  const endItems = relations.map((relation) => ({
    id: relation.endItemId,
    type: relation.endItemType
  }));

  return endItems.map((item) => items.value[item.type][item.id]);
});

const wayImpulses = computed(() => {
  const inspectorView = useInspectorView();
  const simulation = inspectorView.itemStateChangeSimulation;
  return simulation.impulses;
});

const absChange = computed(() => {
  return Math.abs(way.value?.startItemChange ?? 0);
});

const valueOperatorLabel = computed(() => {
  const startItemChange = way.value?.startItemChange ?? 0;
  if (startItemChange < 0) return "-";
  else return "+";
});

const maxStartElementChange = computed(() => {
  if (startItem.value == null) return 100;
  return getMaxValueChange(way.value?.startItemChange ?? 0);
});

function addStartItem(item: TargetItem) {
  if (props.wayId == null) {
    console.error("Cannot add start item to way without wayId");
    return;
  }

  wayStore.setStartItem({
    id: props.wayId,
    itemId: item.id,
    itemType: item.type
  });
}
function addItem(item: TargetItem) {
  if (lastWayItem.value == null) {
    return addStartItem(item);
  }

  const relationStore = useRelationStore();
  const relation = relationStore.getBetweenItems(lastWayItem.value, item);

  console.log(lastWayItem.value, item, relation);

  if (relation == null) {
    console.error(
      "There's no relation between",
      lastWayItem.value.type,
      lastWayItem.value.id,
      "and",
      item.type,
      item.id
    );
    return;
  }

  addRelation(relation);
}

function addRelation(relation: RelationItem) {
  if (!lastWayItem.value) return;
  if (props.wayId == null) {
    console.error("Cannot add relation to way without wayId");
    return;
  }

  // only add relations outgoing of the active element
  if (lastWayItem.value.id !== relation.startItemId) {
    console.warn(
      "relation does not start at correct element",
      relation,
      lastWayItem.value
    );
    return;
  }

  wayStore.addRelation({
    id: props.wayId,
    relationId: relation.id
  });
}

function removeStartItem(mouseEvent: MouseEvent & Handleable) {
  if (props.wayId == null) {
    console.error("Cannot remove start item from way without wayId");
    return;
  }

  mouseEvent.hasBeenHandled = true;
  wayStore.setStartItem({
    id: props.wayId,
    itemId: null,
    itemType: "target"
  });
}

function removeLastRelation(mouseEvent: MouseEvent & Handleable) {
  if (props.wayId == null) {
    console.error("Cannot remove last relation from way without wayId");
    return;
  }

  mouseEvent.hasBeenHandled = true;
  wayStore.removeLastRelation({
    id: props.wayId
  });
}

function openInOverlay(
  mouseEvent: MouseEvent & Handleable,
  item: OverlayDialogItemRequest | null
) {
  if (mouseEvent.hasBeenHandled) return;
  useOverlay().setItem(item);
}

function switchValueOperator() {
  setStartItemChange(-(way.value?.startItemChange ?? 0), false);
}

function setStartItemChange(value: number, setSign = true) {
  if (way.value == null) return;
  const absValue = Math.abs(value);
  if (setSign) {
    const sign = Math.sign(way.value?.startItemChange ?? 0) || 1;
    value = absValue * sign;
  }
  const maxValue = getMaxValueChange(value);
  if (absValue > maxValue) {
    value = maxValue * Math.sign(value);
  }

  wayStore.setStartItemChange({
    id: way.value.id,
    startItemChange: value
  });
}

const throttledSetStartItemChange = throttle(setStartItemChange, 300);

function getMaxValueChange(value: number) {
  if (startItem.value == null) return 0;
  if (value > 0) return 100 - startItem.value.value;
  else if (value < 0) return startItem.value.value;
  else return 100;
}

function setWayComplete(value: boolean) {
  if (way.value == null) return;
  wayStore.setComplete({
    id: way.value.id,
    complete: value
  });
}
</script>

<style lang="scss">
.waylist {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__relation {
    width: calc(100% - 1rem);
    margin-left: 1rem;
  }

  &__changeimpulse,
  &__changeimpulseinfo {
    display: grid;
    gap: 0.25rem;
    margin: 0.5rem 0;
  }

  &__changeimpulse {
    grid-template-columns: 1fr 2.5rem 3rem 1rem;
    padding-left: 0.75rem;
    align-items: center;
  }

  &__changeimpulseinfo {
    grid-template-columns: 1fr 3rem 1rem;
  }
}
</style>
